import { useMemo } from "react";
import { DealData } from "@/pages/deals/types";
import { CompanyData } from "@/pages/companies/types";
import { PersonData } from "@/entities/people/types";
import { LeadData } from "@/pages/leads/types";
import { ContractData } from "@/pages/contracts/types";
import { PartnerData } from "@/pages/partners/types";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ComputedFields } from "@/services/api/types";
import { useDeals } from "@/pages/deals/hooks";
import { useLeads } from "@/pages/leads/hooks";
import { usePeople } from "@/entities/people/hooks";
import { useCompanies } from "@/pages/companies/hooks";
import { useContract } from "@/pages/contracts/hooks";
import { usePartners } from "@/pages/partners/hooks";
import { usePartnerContacts } from "@/pages/partners/contacts/hooks";

export type FullEntityData =
  | DealData
  | LeadData
  | PersonData
  | CompanyData
  | ContractData
  | PartnerData
  | PartnerContactData;

interface UseFullEntityDataResult {
  entity: FullEntityData | undefined;
  isLoading: boolean;
}

export const useFullEntityData = (
  entityId: string,
  entityType: EntityType
): UseFullEntityDataResult => {
  const dealResult = useDeals(
    entityType === EntityType.DEAL ? [entityId] : [],
    {
      enabled: entityType === EntityType.DEAL && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  const leadResult = useLeads(
    entityType === EntityType.LEAD ? [entityId] : [],
    {
      enabled: entityType === EntityType.LEAD && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  const personResult = usePeople(
    entityType === EntityType.PERSON ? [entityId] : [],
    {
      enabled: entityType === EntityType.PERSON && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  const companyResult = useCompanies(
    entityType === EntityType.COMPANY ? [entityId] : [],
    {
      enabled: entityType === EntityType.COMPANY && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  const contractResult = useContract(
    entityType === EntityType.CONTRACT ? entityId : undefined,
    {
      enabled: entityType === EntityType.CONTRACT && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  const partnerResult = usePartners(
    entityType === EntityType.PARTNER ? [entityId] : [],
    {
      enabled: entityType === EntityType.PARTNER && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  const partnerContactResult = usePartnerContacts(
    entityType === EntityType.PARTNER_CONTACT ? [entityId] : [],
    {
      enabled: entityType === EntityType.PARTNER_CONTACT && !!entityId,
      computed_fields: ComputedFields.ALL
    }
  );

  return useMemo(() => {
    switch (entityType) {
      case EntityType.DEAL:
        return {
          entity: dealResult.entities[0] as DealData | undefined,
          isLoading: !dealResult.isSuccess
        };
      case EntityType.LEAD:
        return {
          entity: leadResult.entities[0] as LeadData | undefined,
          isLoading: !leadResult.isSuccess
        };
      case EntityType.PERSON:
        return {
          entity: personResult.entities[0] as PersonData | undefined,
          isLoading: !personResult.isSuccess
        };
      case EntityType.COMPANY:
        return {
          entity: companyResult.entities[0] as CompanyData | undefined,
          isLoading: !companyResult.isSuccess
        };
      case EntityType.CONTRACT:
        return {
          entity: contractResult.data as ContractData | undefined,
          isLoading: !contractResult.isSuccess
        };
      case EntityType.PARTNER:
        return {
          entity: partnerResult.entities[0] as PartnerData | undefined,
          isLoading: !partnerResult.isSuccess
        };
      case EntityType.PARTNER_CONTACT:
        return {
          entity: partnerContactResult.entities[0] as
            | PartnerContactData
            | undefined,
          isLoading: !partnerContactResult.isSuccess
        };
      default:
        return { entity: undefined, isLoading: false };
    }
  }, [
    entityType,
    dealResult.entities,
    dealResult.isSuccess,
    leadResult.entities,
    leadResult.isSuccess,
    personResult.entities,
    personResult.isSuccess,
    companyResult.entities,
    companyResult.isSuccess,
    contractResult.data,
    contractResult.isSuccess,
    partnerResult.entities,
    partnerResult.isSuccess,
    partnerContactResult.entities,
    partnerContactResult.isSuccess
  ]);
};
